import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  InfoSection,
  PropertySectionHeader as SectionHeader,
  PropertySectionTitle as SectionTitle,
  SectionIcon,
  SectionCount,
  SectionContent,
  PropertyDataGrid as DataGrid,
  PropertyDataRow as DataRow,
  DataLabel,
  DataValue,
  ExpandableList,
  ExpandableItem,
  ExpandableHeader,
  ExpandableTitle,
  ExpandableSubtitle,
  ExpandableContent,
  ExpandableDetails,
} from '../../../molecules/PropertyPanelStyles'
import { ExpandArrow } from '../../../atoms/ExpandArrow'
import { Icon } from '../../../atoms/Icon'
import { formatNumberSwiss } from '../../../../utils/number/formatUtils'
import { AdministrativeDetailsSectionProps } from './types'

const EGIDPrefix = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-right: ${({ theme }) => theme.spacing.xsmall};
`

const CountBadge = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.tertiary};
  background: ${({ theme }) => theme.colors.background.tertiary};
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
`

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.large};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
`

const AddressesSubsection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.medium};
`

const AddressesLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

const AddressItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.small};
  margin-bottom: ${({ theme }) => theme.spacing.default};

  &:last-child {
    margin-bottom: 0;
  }
`

const AddressContent = styled.div`
  flex: 1;
`

const AddressStreet = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
`

const AddressInfo = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const AdministrativeDetailsSection: React.FC<
  AdministrativeDetailsSectionProps
> = ({ building }) => {
  const { t } = useTranslation('explore')

  const administrativeBuildings = building.buildings_administrative || []

  const [expandedBuildings, setExpandedBuildings] = useState<Set<string>>(
    new Set()
  )

  const toggleBuilding = (id: string) => {
    const newExpanded = new Set(expandedBuildings)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedBuildings(newExpanded)
  }

  const formatYearRange = (
    min?: number,
    max?: number,
    single?: number
  ): string => {
    if (single) return single.toString()
    if (min && max && min !== max) return `${min} - ${max}`
    if (min) return min.toString()
    if (max) return max.toString()
    return '-'
  }

  return (
    <InfoSection>
      <SectionHeader>
        <SectionTitle>
          <SectionIcon>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z" />
            </svg>
          </SectionIcon>
          {t('buildingPanel.federalRegistry.title')}
        </SectionTitle>
        {administrativeBuildings.length > 0 && (
          <SectionCount>{administrativeBuildings.length}</SectionCount>
        )}
      </SectionHeader>
      <SectionContent>
        {administrativeBuildings.length > 0 ? (
          <ExpandableList>
            {administrativeBuildings.map(
              (adminBuilding: any, index: number) => {
                const uniqueId = String(
                  adminBuilding.building_administrative_id || index
                )
                const isExpanded = expandedBuildings.has(uniqueId)
                const addresses = adminBuilding.addresses || []
                const firstAddress = addresses[0]
                const addressCount = addresses.length

                const formatCompactStreet = (
                  street?: string,
                  streetNb?: string
                ) => {
                  if (!street) return ''
                  let compactStreet = street
                  if (street.length > 20) {
                    compactStreet = street.substring(0, 17) + '...'
                  }
                  return streetNb
                    ? `${compactStreet} ${streetNb}`
                    : compactStreet
                }

                const compactAddress = firstAddress
                  ? formatCompactStreet(
                      firstAddress.street,
                      firstAddress.street_nb
                    )
                  : ''

                return (
                  <ExpandableItem key={uniqueId} $expanded={isExpanded}>
                    <ExpandableHeader onClick={() => toggleBuilding(uniqueId)}>
                      <ExpandableTitle>
                        {adminBuilding.egid && (
                          <>
                            <EGIDPrefix>EGID</EGIDPrefix>
                            {adminBuilding.egid}
                          </>
                        )}
                        {compactAddress && (
                          <>
                            {' \u2022 '}
                            {compactAddress}
                          </>
                        )}
                        {addressCount > 1 && ` +${addressCount - 1}`}
                      </ExpandableTitle>
                      <ExpandableSubtitle>
                        <CountBadge>
                          {index + 1}/{administrativeBuildings.length}
                        </CountBadge>
                        <ExpandArrow $expanded={isExpanded} direction="down" />
                      </ExpandableSubtitle>
                    </ExpandableHeader>

                    <ExpandableContent $expanded={isExpanded}>
                      <ExpandableDetails>
                        <DataGrid $gap={0}>
                          {adminBuilding.name && (
                            <DataRow>
                              <DataLabel>
                                {t(
                                  'buildingPanel.administrativeDetails.buildingName'
                                )}
                              </DataLabel>
                              <DataValue>{adminBuilding.name}</DataValue>
                            </DataRow>
                          )}

                          {adminBuilding.official_number && (
                            <DataRow>
                              <DataLabel>
                                {t(
                                  'buildingPanel.administrativeDetails.officialNumber'
                                )}
                              </DataLabel>
                              <DataValue>
                                {adminBuilding.official_number}
                              </DataValue>
                            </DataRow>
                          )}

                          {(adminBuilding.building_year ||
                            adminBuilding.building_year_min ||
                            adminBuilding.building_year_max) && (
                            <DataRow>
                              <DataLabel>
                                {t(
                                  'buildingPanel.administrativeDetails.yearRange'
                                )}
                              </DataLabel>
                              <DataValue>
                                {formatYearRange(
                                  adminBuilding.building_year_min,
                                  adminBuilding.building_year_max,
                                  adminBuilding.building_year
                                )}
                              </DataValue>
                            </DataRow>
                          )}

                          {(adminBuilding.renovation_year ||
                            adminBuilding.renovation_year_min ||
                            adminBuilding.renovation_year_max) && (
                            <DataRow>
                              <DataLabel>
                                {t(
                                  'buildingPanel.administrativeDetails.renovationRange'
                                )}
                              </DataLabel>
                              <DataValue>
                                {formatYearRange(
                                  adminBuilding.renovation_year_min,
                                  adminBuilding.renovation_year_max,
                                  adminBuilding.renovation_year
                                )}
                              </DataValue>
                            </DataRow>
                          )}

                          {adminBuilding.category && (
                            <DataRow>
                              <DataLabel>
                                {t('common.landRadarInfo.category')}
                              </DataLabel>
                              <DataValue>{adminBuilding.category}</DataValue>
                            </DataRow>
                          )}
                        </DataGrid>

                        {addresses.length > 0 && (
                          <AddressesSubsection>
                            <AddressesLabel>
                              {t(
                                'buildingPanel.federalRegistry.addressesLabel'
                              )}{' '}
                              ({addresses.length})
                            </AddressesLabel>
                            {addresses.map(
                              (address: any, addressIndex: number) => {
                                const dwellingCount =
                                  address.dwellings?.length || 0
                                const streetDisplay = address.street
                                  ? `${address.street}${address.street_nb ? ` ${address.street_nb}` : ''}`
                                  : ''

                                return (
                                  <AddressItem
                                    key={address.address_id || addressIndex}
                                  >
                                    <Icon
                                      name="map-pin"
                                      size="small"
                                      color="currentColor"
                                    />
                                    <AddressContent>
                                      {streetDisplay && (
                                        <AddressStreet>
                                          {streetDisplay}
                                        </AddressStreet>
                                      )}
                                      {dwellingCount > 0 && (
                                        <AddressInfo>
                                          {dwellingCount}{' '}
                                          {dwellingCount === 1
                                            ? t(
                                                'buildingPanel.dwellings.dwellingNumber'
                                              ).toLowerCase()
                                            : t(
                                                'buildingPanel.administrativeDetails.units'
                                              )}
                                          {adminBuilding.area && (
                                            <>
                                              {' \u2022 '}
                                              {formatNumberSwiss(
                                                adminBuilding.area
                                              )}{' '}
                                              m²
                                            </>
                                          )}
                                        </AddressInfo>
                                      )}
                                    </AddressContent>
                                  </AddressItem>
                                )
                              }
                            )}
                          </AddressesSubsection>
                        )}
                      </ExpandableDetails>
                    </ExpandableContent>
                  </ExpandableItem>
                )
              }
            )}
          </ExpandableList>
        ) : (
          <EmptyState>
            {t('buildingPanel.emptyStates.noAdministrativeDetails')}
          </EmptyState>
        )}
      </SectionContent>
    </InfoSection>
  )
}
